// packages/shared-hooks/src/clients/useClientVersions.ts
//
// `GET /clients/:id/versions` — cursor-paginated version history (PII-free
// metadata only; per-version snapshot lives behind `useClientVersion`). The
// detail page uses the infinite variant to allow load-more on aggregates
// with 1000+ versions.
import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import {
  GetClientVersionsResponseSchema,
  type GetClientVersionsQuery,
  type GetClientVersionsResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { clientsKeys } from './keys.js';

export interface ClientVersionsFilter extends Partial<GetClientVersionsQuery> {
  cursor?: string;
}

function toQuery(filter: ClientVersionsFilter): Record<string, string | number | undefined> {
  return { cursor: filter.cursor, limit: filter.limit };
}

export function useClientVersions(clientId: string | null, filter: ClientVersionsFilter = {}) {
  const client = useApiClient();
  return useQuery<GetClientVersionsResponse>({
    queryKey: clientId
      ? [...clientsKeys.versions(clientId), filter]
      : ['clients', '__none__', 'versions'],
    queryFn: ({ signal }) => {
      if (!clientId) throw new Error('useClientVersions called without clientId');
      return client.get<GetClientVersionsResponse>(
        `/clients/${clientId}/versions`,
        { signal, query: toQuery(filter) },
        GetClientVersionsResponseSchema,
      );
    },
    enabled: clientId !== null,
  });
}

export function useClientVersionsInfinite(clientId: string | null) {
  const client = useApiClient();
  return useInfiniteQuery<
    GetClientVersionsResponse,
    Error,
    InfiniteData<GetClientVersionsResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: clientId
      ? [...clientsKeys.versions(clientId), 'infinite']
      : ['clients', '__none__', 'versions', 'infinite'],
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) => {
      if (!clientId) throw new Error('useClientVersionsInfinite called without clientId');
      return client.get<GetClientVersionsResponse>(
        `/clients/${clientId}/versions`,
        { signal, query: { cursor: pageParam } },
        GetClientVersionsResponseSchema,
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: clientId !== null,
  });
}
